import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Fade from '@mui/material/Fade';
import LinearProgress from '@mui/material/LinearProgress';
import Paper from '@mui/material/Paper';
import Step from '@mui/material/Step';
import StepButton from '@mui/material/StepButton';
import Stepper from '@mui/material/Stepper';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import { useSnackbar } from 'notistack';
import {
  Suspense,
  lazy,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';

import axios from 'axios';

import { FormDataProvider, useFormDataContext } from 'context/FormDataContext';

import { useUser } from 'hooks/useUser';

import {
  INITIAL_FORM_DATA,
  type FormData,
  mergeFormDataFromApiPayload,
} from 'types/formData';

import { postCreateReport } from 'utils/createReportPayload';
import { isFormDataEqual } from 'utils/formEquality';

import { UserType } from 'routes/Dashboard';

import type { ReportFormMode } from './types';

import styles from './styles';

const PacientData = lazy(() => import('./PacientData'));
const ClinicHistory = lazy(() => import('./ClinicHistory'));
const PhysicalExam = lazy(() => import('./PhysicalExam'));
const Avaliation = lazy(() => import('./Avaliation'));
const Diagnostic = lazy(() => import('./Diagnostic'));

const STEP_LABELS = [
  'Ficha de atendimento',
  'História clínica',
  'Exames físicos',
  'Avaliação',
  'Diagnóstico e tratamento',
] as const;

const LAST_STEP_INDEX = STEP_LABELS.length - 1;

enum ReportStepIndex {
  Pacient,
  Clinic,
  Physical,
  AvaliationForm,
  DiagnosticForm,
}

const ReportContent = (): JSX.Element => {
  const { user } = useUser();
  const { enqueueSnackbar } = useSnackbar();
  const { loadFromApi, wipeData, data: formData } = useFormDataContext();
  const [activeStep, setActiveStep] = useState(0);
  const [pacientData, setPacientData] = useState<FormData | undefined>();
  const [baseline, setBaseline] = useState<FormData | null>(null);
  const [reportFetchLoading, setReportFetchLoading] = useState(false);
  const [footerSaveLoading, setFooterSaveLoading] = useState(false);
  const { state } = useLocation() as {
    state?: { id?: string | number } | null;
  };

  const mode: ReportFormMode = state?.id ? 'edit' : 'create';
  const editingPacientId = state?.id;

  const reportFormForTabs = useMemo(
    () => (state?.id ? pacientData : INITIAL_FORM_DATA),
    [pacientData, state?.id],
  );

  const disableField = user.type === UserType.PACIENT;

  const isDirty = useMemo(() => {
    if (mode !== 'edit' || baseline === null || disableField) {
      return false;
    }
    return !isFormDataEqual(formData, baseline);
  }, [baseline, disableField, formData, mode]);

  useLayoutEffect(() => {
    if (state?.id) {
      return;
    }
    wipeData();
    setPacientData(undefined);
    setBaseline(null);
  }, [state?.id, wipeData]);

  const fetchData = useCallback(async () => {
    const pacientId = state?.id;
    if (pacientId === undefined || pacientId === null) {
      return;
    }
    setReportFetchLoading(true);
    setBaseline(null);
    try {
      const response = await axios.get<FormData>(`/get-report`, {
        params: {
          pacientId,
        },
      });
      const normalized = mergeFormDataFromApiPayload(response.data);
      // Mesmo shape do contexto após loadFromApi — evita dirty falso ao sincronizar abas.
      setPacientData(normalized);
      loadFromApi(response.data);
      setBaseline(JSON.parse(JSON.stringify(normalized)) as FormData);
    } finally {
      setReportFetchLoading(false);
    }
  }, [loadFromApi, state]);

  useEffect(() => {
    if (!state?.id) {
      return;
    }
    fetchData().catch(() => {
      enqueueSnackbar('Não foi possível carregar o relatório.', {
        variant: 'error',
      });
    });
  }, [enqueueSnackbar, fetchData, state?.id]);

  const handleSaveEdits = useCallback(async () => {
    if (!isDirty || disableField) {
      return;
    }
    setFooterSaveLoading(true);
    try {
      await postCreateReport(formData, user.id, {
        pacientId: editingPacientId,
      });
      enqueueSnackbar('Alterações salvas com sucesso');
      setBaseline(JSON.parse(JSON.stringify(formData)) as FormData);
    } catch {
      enqueueSnackbar('Erro ao salvar alterações', { variant: 'error' });
    } finally {
      setFooterSaveLoading(false);
    }
  }, [
    disableField,
    editingPacientId,
    enqueueSnackbar,
    formData,
    isDirty,
    user.id,
  ]);

  const handleChangeTab = useCallback(
    (_event: React.SyntheticEvent, newValue: number) => {
      setActiveStep(newValue);
    },
    [],
  );

  const handleStep = useCallback((step: number) => {
    setActiveStep(step);
  }, []);

  const goPrev = useCallback(() => {
    setActiveStep(s => Math.max(0, s - 1));
  }, []);

  const goNext = useCallback(() => {
    setActiveStep(s => Math.min(LAST_STEP_INDEX, s + 1));
  }, []);

  const activePanel = useMemo(() => {
    const common = {
      mode,
      editingPacientId,
    };
    switch (activeStep) {
      case ReportStepIndex.Pacient:
        return (
          <PacientData
            value={activeStep}
            id={ReportStepIndex.Pacient}
            disableField={disableField}
            pacientData={reportFormForTabs}
            {...common}
          />
        );
      case ReportStepIndex.Clinic:
        return (
          <ClinicHistory
            value={activeStep}
            id={ReportStepIndex.Clinic}
            disableField={disableField}
            pacientData={reportFormForTabs}
            {...common}
          />
        );
      case ReportStepIndex.Physical:
        return (
          <PhysicalExam
            value={activeStep}
            id={ReportStepIndex.Physical}
            disableField={disableField}
            pacientData={reportFormForTabs}
            {...common}
          />
        );
      case ReportStepIndex.AvaliationForm:
        return (
          <Avaliation
            value={activeStep}
            id={ReportStepIndex.AvaliationForm}
            disableField={disableField}
            pacientData={reportFormForTabs}
            {...common}
          />
        );
      case ReportStepIndex.DiagnosticForm:
        return (
          <Diagnostic
            value={activeStep}
            id={ReportStepIndex.DiagnosticForm}
            disableField={disableField}
            pacientData={reportFormForTabs}
            {...common}
          />
        );
      default:
        return null;
    }
  }, [activeStep, disableField, editingPacientId, mode, reportFormForTabs]);

  const showEditFooter =
    mode === 'edit' &&
    !disableField &&
    baseline !== null &&
    (isDirty || footerSaveLoading);

  return (
    <Box
      sx={[
        styles.container,
        styles.reportLayout,
        showEditFooter ? styles.reportLayoutFooterPad : false,
      ]}
    >
      {reportFetchLoading && (
        <LinearProgress aria-busy aria-label="Carregando relatório" />
      )}

      <Stepper
        nonLinear
        activeStep={activeStep}
        alternativeLabel
        sx={styles.stepper}
      >
        {STEP_LABELS.map((label, index) => (
          <Step key={label} completed={index < activeStep}>
            <StepButton color="inherit" onClick={() => handleStep(index)}>
              {label}
            </StepButton>
          </Step>
        ))}
      </Stepper>

      <Box sx={styles.tabs}>
        <Tabs
          value={activeStep}
          onChange={handleChangeTab}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
        >
          {STEP_LABELS.map((label, index) => (
            <Tab
              key={label}
              label={label}
              id={`tab-reports-${index}`}
              aria-controls={`tabpanel-reports-${index}`}
            />
          ))}
        </Tabs>
      </Box>

      <Box sx={styles.stepNavRow}>
        <Button
          type="button"
          variant="outlined"
          onClick={goPrev}
          disabled={activeStep === 0}
        >
          Anterior
        </Button>
        <Button
          type="button"
          variant="outlined"
          onClick={goNext}
          disabled={activeStep === LAST_STEP_INDEX}
        >
          Próxima
        </Button>
      </Box>

      <Fade in appear key={activeStep} timeout={240}>
        <Box
          role="tabpanel"
          id={`tabpanel-reports-${activeStep}`}
          aria-labelledby={`tab-reports-${activeStep}`}
          sx={styles.tabpanel}
          aria-busy={reportFetchLoading}
        >
          <Suspense
            fallback={
              <Box sx={styles.suspenseFallback}>
                <CircularProgress aria-label="Carregando etapa" />
              </Box>
            }
          >
            {activePanel}
          </Suspense>
        </Box>
      </Fade>

      {showEditFooter ? (
        <Box component="footer" sx={styles.editFooterWrap}>
          <Paper elevation={8} sx={styles.editFooterPaper}>
            <Button
              variant="contained"
              disabled={footerSaveLoading}
              onClick={() => {
                // eslint-disable-next-line no-void
                void handleSaveEdits();
              }}
              aria-label="Salvar alterações do relatório"
            >
              {footerSaveLoading ? (
                <CircularProgress size={22} color="inherit" />
              ) : (
                'Salvar alterações'
              )}
            </Button>
          </Paper>
        </Box>
      ) : null}
    </Box>
  );
};

const Report = (): JSX.Element => (
  <FormDataProvider>
    <ReportContent />
  </FormDataProvider>
);

export default Report;
